import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { PermissionsService } from '../services/permissions.service';
import { SesionResponse, Usuario } from './auth.models';

const TOKEN_KEY = 'silabvet_token';
/**
 * TEMPORAL — se marca al cerrar sesión para que el siguiente login pida
 * credenciales. SIGERH no tiene logout y su cookie vive 8 h, así que sin esto
 * el SSO reabre la sesión del mismo usuario sin preguntar nada. La bandera se
 * limpia recién cuando hay sesión, no al lanzar el login: si el usuario
 * abandona el formulario de SIGERH, el siguiente intento sigue forzando.
 */
const FORZAR_LOGIN_KEY = 'silabvet_forzar_login';

/**
 * Estado de autenticación de SiLabVet. El login ocurre en SIGERH (SSO): el
 * `login()` navega al backend, que redirige a SIGERH; al volver, la
 * `CallbackPage` entrega el `code` a `handleCallback()`. El token de sesión se
 * guarda en localStorage y viaja en cada request vía el interceptor.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly permissions = inject(PermissionsService);
  private readonly baseUrl = `${environment.apiUrl}/auth`;

  private readonly token = signal<string | null>(localStorage.getItem(TOKEN_KEY));
  private readonly _usuario = signal<Usuario | null>(null);

  readonly usuario = this._usuario.asReadonly();
  readonly isAuthenticated = computed(() => !!this.token());

  /** Token actual (lo usa el interceptor). */
  getToken(): string | null {
    return this.token();
  }

  /** Redirige a SIGERH para iniciar sesión (full-page, no XHR). */
  login(): void {
    const forzar = localStorage.getItem(FORZAR_LOGIN_KEY) === '1';
    window.location.href = `${this.baseUrl}/login${forzar ? '?forzar=1' : ''}`;
  }

  /** Cambia el `code` del callback por una sesión y la deja activa. */
  handleCallback(code: string): Observable<SesionResponse> {
    return this.http
      .post<SesionResponse>(`${this.baseUrl}/callback`, { code })
      .pipe(tap((sesion) => this.establecerSesion(sesion)));
  }

  /**
   * Al arrancar la app, si hay token guardado recupera el usuario y sus
   * permisos. Si el token ya no sirve, limpia la sesión sin romper el arranque.
   */
  restaurar(): Observable<unknown> {
    if (!this.token()) {
      return of(null);
    }
    return this.http.get<SesionResponse>(`${this.baseUrl}/me`).pipe(
      tap((sesion) => this.establecerSesion(sesion)),
      catchError(() => {
        this.limpiarSesion();
        return of(null);
      }),
    );
  }

  /** Guarda (o reemplaza) la firma digitalizada del usuario para futuros usos. */
  guardarFirma(firma: string): Observable<Usuario> {
    return this.http
      .put<Usuario>(`${this.baseUrl}/firma`, { firma })
      .pipe(tap((usuario) => this._usuario.set(usuario)));
  }

  /** Quita la firma guardada del usuario. */
  eliminarFirma(): Observable<Usuario> {
    return this.http
      .delete<Usuario>(`${this.baseUrl}/firma`)
      .pipe(tap((usuario) => this._usuario.set(usuario)));
  }

  /** Cierra la sesión en el backend y vuelve a la página de login. */
  logout(): void {
    this.http.post(`${this.baseUrl}/logout`, {}).subscribe({
      complete: () => this.finalizarLogout(),
      error: () => this.finalizarLogout(),
    });
  }

  /**
   * Navega al `/login` local en vez de relanzar el SSO: si volviéramos a
   * SIGERH, su cookie de sesión sigue viva y reabriría la sesión al instante.
   * Por eso además deja marcado que el próximo login pida credenciales.
   */
  private finalizarLogout(): void {
    this.limpiarSesion();
    localStorage.setItem(FORZAR_LOGIN_KEY, '1');
    void this.router.navigateByUrl('/login');
  }

  /** Limpia el estado local (lo llama también el interceptor ante un 401). */
  limpiarSesion(): void {
    localStorage.removeItem(TOKEN_KEY);
    this.token.set(null);
    this._usuario.set(null);
    this.permissions.limpiar();
  }

  private establecerSesion(sesion: SesionResponse): void {
    localStorage.removeItem(FORZAR_LOGIN_KEY);
    localStorage.setItem(TOKEN_KEY, sesion.token);
    this.token.set(sesion.token);
    this._usuario.set(sesion.usuario);
    this.permissions.cargar(sesion.permisos);
  }
}
